import { createSignal, Show, useContext } from 'solid-js';
import { AppContext } from '../../app-context-provider/app-context-provider';
import { getBackgroundImageSrcData, ImageSrcData } from '../image-component/image-component';
import theme, { breakpoints } from '../../style/theme';
import { Button } from '../../ui-components/button/button';
import { Heading } from '../../ui-components/heading/heading';
import { Text } from '../../ui-components/text/text';
import { HeroComponentProps } from './hero-campaign-types';
import { Grid } from '../../grid-system/grid/grid';
import { AngleDownIcon } from '../icons-library/angle-down';
import { focusPosition } from '../../tools/get-image-focus';
import { ErrorCatcher } from '../../tools/error-catcher';
import { PlayCircleIcon } from '../icons-library/play-circle';
import { VideoModal } from '../video-modal/video-modal';
import clsx from 'clsx';
import { TabHandler } from '../../tools/tab-handler';

const getOverlayClass = (overlayType?: string) => {
    if (overlayType === 'on') return 'hero-overlay-on';
    if (overlayType === 'auto') return 'hero-overlay-auto';
    return '';
};

/**
 * This is a simple copy of the Hero component with one change: the CTA button is no longer hard-coded
 * to the text "Learn more" and the action being scrolling down the page, but instead customizable in the block.
 */

export const HeroCampaign = (props: HeroComponentProps) => {
    const { imagesServiceUrl, supportedImageFormats, localize, viewport, siteInfo } = useContext(AppContext);

    const [ showModal, setShowModal ] = createSignal(false);

    const [ firstTabbableElement, setFirstTabbableElement ] = createSignal<HTMLElement | undefined>();
    const [ lastTabbableElement, setLastTabbableElement ] = createSignal<HTMLElement | undefined>();

    const isMobile = () => viewport.width <= breakpoints.MOBILE;

    const imageWidth = () => {
        if (viewport.width <= breakpoints.TABLET_SMALL) {
            return 750;
        }
        if (viewport.width <= breakpoints.DESKTOP && viewport.width > breakpoints.TABLET_SMALL) {
            return 1500;
        }
        return 1920;
    };

    const imageHeight = () => {
        if (viewport.width <= breakpoints.TABLET_SMALL) {
            return 325;
        }
        if (viewport.width <= breakpoints.DESKTOP && viewport.width > breakpoints.TABLET_SMALL) {
            return 750;
        }
        return 960;
    };

    const getBackgroundImage = () => {
        if (props.src) {
            const backgroundImage: ImageSrcData = getBackgroundImageSrcData({
                imagesServiceUrl,
                supportedImageFormats,
                src: props.src,
                focus: props.focus,
                sign: props.sign,
                width: imageWidth() || 750,
                height: imageHeight() || 325,
            });
            return backgroundImage;
        }
    };

    const customCssRow = `
        height: 100%;
        ${theme.mediaQueries.mobileMax} {
            height: auto;
        }
    `;

    const handleScrollDown = (e: any) => {
        e.preventDefault();
        if (!window) {
            return;
        }

        window.scrollTo({
            top: window.innerHeight - (isMobile() ? 20 : 100),
            behavior: 'smooth'
        });
    };

    const bgImage = () => getBackgroundImage();

    const wrapperBgStyle = () => {
        const img = bgImage();
        if (img && img.original !== '') {
            return {
                'background-image': `url(${img.original})`,
                'background-repeat': 'no-repeat',
                'background-size': 'cover',
                'background-position': focusPosition(props.focus) || 'center',
            };
        }
        return {};
    };

    const mobileImageBgStyle = () => {
        const img = bgImage();
        if (img && img.original !== '') {
            return {
                'background-image': `url(${img.original})`,
                'background-position': focusPosition(props.focus) || 'center',
                'background-repeat': 'no-repeat',
                'background-size': 'cover',
            };
        }
        return {};
    };

    const heroModalCta = () => {
        let desktopBottomOffset = '';

        if (props.description && props?.customLink?.url) {
            desktopBottomOffset = '120px';
        } else if (props.description || props?.customLink?.url) {
            desktopBottomOffset = '0px';
        } else {
            desktopBottomOffset = '-150px';
        }

        return (
            <div
                class="bg-white p-[3rem_3.5rem] rounded-[1rem] h-max absolute right-0 max-[1115px]:relative max-[1115px]:self-start max-[1115px]:bottom-0 max-[1115px]:mb-12"
                style={{ bottom: desktopBottomOffset }}
            >
                <Text
                    color={'darkGray'}
                    fontSize={isMobile() ? 'normal' : 'large'}
                    displayRedVerticalLine={false}
                >
                    {props.videoDescription}
                </Text>
                <button
                    class="flex flex-row items-center gap-6 [&>svg]:transition-[transform,color,fill] [&>svg]:duration-200 hover:[&>button]:text-primary-hover hover:[&>svg]:scale-110 hover:[&>svg]:fill-primary-hover"
                    onClick={() => setShowModal(true)}
                    aria-label={localize('open-video', 'Open video')}
                >
                    <PlayCircleIcon width={3} height={3} />
                    <div class="underline text-normal tracking-[0] leading-[2.3rem] font-atos-bold font-[800] text-primary transition-colors duration-200 cursor-pointer hover:text-primary-hover">
                        {props.ctaText || ''}
                    </div>
                </button>
            </div>
        );
    };

    return (
        <ErrorCatcher componentName="HeroCampaign">
            <div class='hero-campaign relative'>
                <div
                    class={clsx(
                        'hero-wrapper relative flex flex-col h-auto min-h-screen',
                        'max-mobile:bg-none',
                        getOverlayClass(props.overlayType),
                    )}
                    style={wrapperBgStyle()}
                >
                    <Grid
                        templateShorthand={[12]}
                        isLayoutGrid={true}
                        customCss={`
                            height: 100%; ${isMobile() ? 'grid-template-rows: max-content;' : ''}
                        `}
                    >
                        <Grid templateShorthand={[1, 11]} responsive={{ tablet: [1, 11], mobile: [12] }} customCss={customCssRow}>
                            <div class="@max-[480px]:hidden" />
                            <div class="flex flex-col h-full z-[1] pb-8 max-mobile:justify-start">
                                <div
                                    class={clsx(
                                        'flex gap-16 px-6 mt-[23vh] mb-0 relative',
                                        'max-[1115px]:flex-col max-[1115px]:gap-8',
                                        '@max-480:mt-[27%] @max-480:mb-[10%]',
                                        siteInfo?.siteType === 'atos-care' && 'mt-[32vh] @max-480:mt-[calc(82px+27%)] @max-480:mb-[10%]',
                                    )}
                                >

                                    <div>
                                        <div class="max-mobile:text-center max-mobile:mb-12">
                                            <Heading tag="h1" variant="heroEyebrow">
                                                {props.eyebrowHeading}
                                            </Heading>
                                            <Heading tag="h2" variant="hero">
                                                {props.largerHeading}
                                            </Heading>
                                        </div>
                                        <Text color={'darkGray'} fontSize={isMobile() ? 'normal' : 'large'} displayRedVerticalLine={true}>
                                            {props.description}
                                        </Text>
                                        <Show when={props?.customLink?.url}>
                                            <div class="py-[2.14rem]">
                                                <Button
                                                    label={props.customLink.label ? props.customLink.label : ''}
                                                    url={props.customLink.url}
                                                    variant="primary"
                                                    target={props.customLink.target}
                                                    isLink={true}
                                                />
                                            </div>
                                        </Show>
                                    </div>

                                    <Show when={props.withModal && !isMobile()}>
                                        {heroModalCta()}
                                    </Show>
                                </div>
                            </div>
                        </Grid>

                        <div
                            class={clsx(
                                'hero-mobile-image hidden',
                                'max-mobile:flex max-mobile:grow max-mobile:justify-center max-mobile:mx-[-1.14rem]',
                                props.withModal && 'max-mobile:h-[35rem] max-mobile:mt-8 max-mobile:relative',
                            )}
                            style={mobileImageBgStyle()}
                        >
                            <Show when={props.withModal}>
                                <div class="max-mobile:absolute max-mobile:w-screen max-mobile:flex max-mobile:justify-center max-mobile:top-[-4rem]">
                                    {heroModalCta()}
                                </div>
                            </Show>
                            <Show when={!props.withModal}>
                                <div id="learnMoreMobile" onClick={handleScrollDown}>
                                    <Button label={localize('learn-more', 'Learn more')} variant="tertiary" darkMode={true} />
                                    <div class="mt-[0.7rem]">
                                        <AngleDownIcon fill="white" ariaHidden />
                                    </div>
                                </div>
                            </Show>
                        </div>
                    </Grid>

                    <Show when={!props.withModal}>
                        <div class="mx-auto mt-auto mb-12 max-mobile:hidden [&_svg]:cursor-pointer [&_svg]:w-[2.14rem] [&_svg]:h-[2.14rem] [&_svg]:fill-white">
                            <button
                                class="w-14 h-14 flex justify-center items-center border-none bg-transparent"
                                aria-label={localize('learn-more', 'Learn more')}
                                onClick={handleScrollDown}
                            >
                                <AngleDownIcon ariaLabel={localize('learn-more', 'Learn more')} />
                            </button>
                        </div>
                    </Show>
                </div>

                <Show when={showModal()}>
                    <TabHandler refs={{
                        firstTabbableElement,
                        lastTabbableElement,
                    }}>
                        <VideoModal
                            show={true}
                            closeModal={() => setShowModal(false)}
                            video={props.video}
                            modalVideoTitle={props.modalVideoTitle}
                            modalLink={props.modalLink}
                            closeButtonRef={(el: HTMLElement) => setFirstTabbableElement(el)}
                            setLastTabbableElement={(el: HTMLElement) => setLastTabbableElement(el)}
                        />
                    </TabHandler>
                </Show>

            </div>
        </ErrorCatcher>
    );
};

HeroCampaign.parseProps = (atts: any) => {
    const { image, customLink } = atts;
    const commonAtts = {
        altText: atts.altText,
        imageText: atts.imageText,
        eyebrowHeading: atts.eyebrowHeading,
        largerHeading: atts.largerHeading,
        description: atts.description,
        labels: atts.labels,
        customLink: customLink ? customLink[0] : null,
        withModal: atts.withModal,
        videoDescription: atts.videoDescription,
        ctaText: atts.ctaText,
        modalVideoTitle: atts.modalVideoTitle,
        modalLink: atts.modalLink,
        video: {
            ...atts.video,
            videoLink: atts.video?.video_link,
        },
    };

    if (image) {
        return {
            src: image.url,
            width: image.width,
            focus: image.focus,
            sign: image.sign,
            overlayType: atts.overlayType,
            ...commonAtts,
        };
    } else return commonAtts;
};
